import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import UserPicker, { type ActionTypes, type Value } from '@atlaskit/user-picker';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { useInvitePeopleModal } from '@atlassian/jira-invite-people/src/controllers/index.tsx';
import { ASSIGNEE_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { ExperiencePerformanceTypes, ExperienceTypes, UFOExperience } from '@atlassian/ufo';
// eslint-disable-next-line jira/deprecations/ban-identifiers
import { UNSAFE_noExposureExp, expVal } from '@atlassian/jira-feature-experiments';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsEligibleForInviteOnOpen } from '@atlassian/jira-onboarding-core/src/controllers/is-eligible-for-invite-on-open/index.tsx';
import { INVITE_PEOPLE_ID, UNASSIGNED_ID } from '../../../common/constants.tsx';
import type { UserValue } from '../../../common/types.tsx';
import { getUserFromUserOption } from '../../../common/utils.tsx';
import messages from '../../../messages.tsx';
import {
	useAssigneeOptions,
	fireInviteItemRendererEvent,
} from '../../../services/assignee-options/index.tsx';
import useUsersQuery from '../../../services/users-query/index.tsx';
import type { InlineAssigneePickerEditProps } from './types.tsx';

const DEFAULT_SEARCH_DEBOUCE_TIMEOUT = 300;

const InlineAssigneeFieldOptionsLoadExperience = new UFOExperience(
	'inline-assignee-picker.field-options-load',
	{
		type: ExperienceTypes.Operation,
		performanceType: ExperiencePerformanceTypes.Custom,
		category: 'inline-assignee-picker.field-options-load',
	},
);

const isImproveInviteViaAssigneeEnabled = () => {
	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [config] = UNSAFE_noExposureExp('jira_improve_invite_via_assignee');
	return config.get('isEnabled', false);
};

const InlineAssigneePickerEdit = ({
	shouldPreloadAssignToMe = false,
	autoCompleteUrl,
	value = null,
	width,
	isDisabled,
	onFocus,
	onBlur,
	onChange,
	searchDebounceTimeout = DEFAULT_SEARCH_DEBOUCE_TIMEOUT,
	enablePeopleInvite = true,
	enableAutomaticOption = false,
	isSubtle = false,
	appearance = 'normal',
	autoFocus = false,
	inputId,
	openMenuOnClick = false,
	ariaLabel,
	isRequired,
	styles,
	menuPortalTarget,
	components,
	...fieldProps
}: InlineAssigneePickerEditProps) => {
	const intl = useIntl();
	const self = useRef<{
		previousQuery?: string;
	}>({ previousQuery: undefined }).current;
	const [{ data: users, loading, error }, fetchUsers] = useUsersQuery(autoCompleteUrl || '');
	const isEligibleForInviteOnOpen = useIsEligibleForInviteOnOpen();
	const shouldShowInviteOnOpen =
		isEligibleForInviteOnOpen && enablePeopleInvite && isImproveInviteViaAssigneeEnabled();
	const isAdmin = useIsAdmin();
	const [assigneeValue, assigneeOptions, { userOptionsCount }] = useAssigneeOptions({
		value,
		users,
		intl,
		shouldPreloadAssignToMe,
		search: self.previousQuery,
		enablePeopleInvite,
		enableAutomaticOption,
		shouldHideUnassigned: isRequired,
		shouldShowInviteOnOpen,
	});

	const [, { openModal }] = useInvitePeopleModal();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (loading) {
			fireOperationalAnalytics(
				createAnalyticsEvent({
					actionSubject: 'inlineAssigneePicker',
					action: 'loading',
				}),
				'inlineAssigneeOptions loading',
			);
			InlineAssigneeFieldOptionsLoadExperience.start();
		}
		if (error) {
			fireOperationalAnalytics(
				createAnalyticsEvent({
					actionSubject: 'inlineAssigneePicker',
					action: 'failed',
				}),
				'inlineAssigneeOptions failed',
			);
			InlineAssigneeFieldOptionsLoadExperience.failure();
		}
		if (users.length) {
			fireOperationalAnalytics(
				createAnalyticsEvent({
					actionSubject: 'inlineAssigneePicker',
					action: 'loaded',
				}),
				'inlineAssigneeOptions loaded',
			);
			InlineAssigneeFieldOptionsLoadExperience.success();
		}
	}, [loading, error, users, createAnalyticsEvent]);

	const handleSearch: (query?: string) => void = useMemo(
		() =>
			debounce((query?: string) => {
				/*
				 * Added extra condition to skip search. it's getting called during onBlur because previousQuery is undefined and query is empty string
				 * then query !== self.previousQuery will be true and fetchUsers getting called unnecessarily
				 */
				if (query === '' && self.previousQuery === undefined) {
					return;
				}
				query !== self.previousQuery && fetchUsers(query);
				self.previousQuery = query;
			}, searchDebounceTimeout),
		// go/jfe-eslint
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[fetchUsers, searchDebounceTimeout, self.previousQuery],
	);

	const handleOnFocus = useCallback(
		(sessionId?: string) => {
			onFocus?.(sessionId);

			// Exposure is fired here for jira_improve_invite_via_assignee because it is when the Assignee Field is opened
			if (isEligibleForInviteOnOpen && enablePeopleInvite) {
				if (expVal('jira_improve_invite_via_assignee', 'isEnabled', false)) {
					fireInviteItemRendererEvent({
						createAnalyticsEvent,
						suggestionsCount: userOptionsCount,
						isAdmin,
					});
				}
			}
			fireUIAnalytics(createAnalyticsEvent({}), 'inlineAssigneePickerEdit opened', {
				isAdmin,
				enablePeopleInvite,
				shouldShowInviteOnOpen,
			});

			if (!users || !users.length) {
				fetchUsers();
			}
		},
		[
			onFocus,
			enablePeopleInvite,
			users,
			createAnalyticsEvent,
			isAdmin,
			isEligibleForInviteOnOpen,
			shouldShowInviteOnOpen,
			userOptionsCount,
			fetchUsers,
		],
	);

	const handleOnBlur = useCallback(
		(sessionId?: string) => {
			onBlur?.(sessionId);
			self.previousQuery = undefined;
		},
		[onBlur, self],
	);

	const handleOnChange = useCallback(
		(updatedValue: Value, action: ActionTypes) => {
			if (!updatedValue || Array.isArray(updatedValue)) {
				return;
			}

			if (enablePeopleInvite && updatedValue.id === INVITE_PEOPLE_ID) {
				openModal(createAnalyticsEvent ?? null, { inviteFlow: 'assignee' });
				onChange?.(value, action);
				return;
			}

			let newValue: UserValue;

			if (updatedValue.id === UNASSIGNED_ID || updatedValue.id === INVITE_PEOPLE_ID) {
				newValue = null;
			} else {
				newValue = getUserFromUserOption(updatedValue);
			}

			onChange?.(newValue, action);
		},
		[enablePeopleInvite, onChange, openModal, value, createAnalyticsEvent],
	);

	/*
	 * user picker default opens on focus, fetchUsers has to be called onFocus
	 * when openMenuOnClick is enabled then fetchUsers should be called during onOpen rather than onFocus
	 */
	const controlProps = openMenuOnClick
		? { openMenuOnClick, onOpen: handleOnFocus }
		: { onFocus: handleOnFocus };

	return (
		<UserPicker
			inputId={inputId}
			fieldId={ASSIGNEE_TYPE}
			autoFocus={autoFocus}
			placeholder={intl.formatMessage(messages.searchForAssignee)}
			isLoading={loading}
			options={assigneeOptions}
			value={assigneeValue}
			isMulti={false}
			isClearable={false}
			onBlur={handleOnBlur}
			onChange={handleOnChange}
			onInputChange={handleSearch}
			subtle={isSubtle}
			appearance={appearance}
			width={width}
			isDisabled={isDisabled}
			ariaLabelledBy={fieldProps['aria-labelledby']}
			UNSAFE_hasDraggableParentComponent
			ariaLabel={ariaLabel}
			menuPortalTarget={menuPortalTarget}
			styles={styles}
			components={components}
			{...controlProps}
		/>
	);
};

export default InlineAssigneePickerEdit;
